import PropTypes from 'prop-types';
import {
  Container,
  Button,
  LinearProgress,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Scrollbar } from 'components/Scrollbar';
import { useDrawer } from 'contexts/DrawerContext';
import { useArtworkDrawer } from 'contexts/ArtworkDrawerContext';

export const DrawerContent = (props) => {
  const { children } = props;

  return (
    <Container
      maxWidth="lg"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        px: 0,
        '&.MuiContainer-root': {
          px: 1,
        },
      }}
    >
      {children}
    </Container>
  );
};

DrawerContent.propTypes = {
  children: PropTypes.node,
};

export const Wrapper = (props) => {
  const { children } = props;

  return (
    <Stack
      justifyContent="space-between"
      direction="column"
      sx={{
        flex: 1,
        minHeight: 0,
        px: 0,
      }}
    >
      {children}
    </Stack>
  );
};

Wrapper.propTypes = {
  children: PropTypes.node,
};

export const Content = (props) => {
  const { children, pt } = props;

  return (
    <Scrollbar
      sx={{
        flexGrow: 1,
        minHeight: 0,
        height: '100%',
        '& .simplebar-content': {
          height: '100%',
        },
        '& .simplebar-scrollbar:before': {
          background: '#ebebeb',
        },
        paddingRight: '12px',
        paddingTop: pt ?? '4px',
      }}
    >
      <Stack
        spacing={2}
        sx={{ height: '100%', pt: pt ?? 1 }}
      >
        {children}
      </Stack>
    </Scrollbar>
  );
};

Content.propTypes = {
  children: PropTypes.node,
};

export const Header = (props) => {
  const { title = '', hideClose = false, loading = false } = props;
  const { closeDrawer } = useDrawer();
  const { closeDrawer: closeArtworkDrawer } = useArtworkDrawer();

  return (
    <Stack
      spacing={1}
      sx={{ position: 'relative', pb: 0.5 }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pt: 1.5 }}
      >
        <Typography
          component="h6"
          variant="subtitle1"
          noWrap
          sx={{ fontWeight: 500 }}
        >
          {title}
        </Typography>
        {!hideClose && (
          <Tooltip
            title="Close"
            placement="left"
            arrow
          >
            <IconButton
              onClick={() => {
                closeDrawer();
                closeArtworkDrawer();
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {loading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
          }}
        />
      )}
    </Stack>
  );
};

Header.propTypes = {
  title: PropTypes.string,
};

export const ActionButtons = (props) => {
  const {
    closeButtonText = 'Cancel',
    submitButtonText = '',
    submitButtonLoading = false,
    stackDirection = 'row',
    disabled = false,
    submit = () => {},
    close = () => {},
    hideClose = false,
    hideSubmit = false,
  } = props;

  return (
    <Stack
      direction={stackDirection || 'row'}
      spacing={2}
      sx={{ mt: 3, mb: 2 }}
    >
      {!hideClose && (
        <Button
          fullWidth
          variant="ghost"
          color="inherit"
          onClick={close}
          sx={{ textTransform: 'uppercase', letterSpacing: 1.2 }}
        >
          {closeButtonText}
        </Button>
      )}
      {!hideSubmit && (
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={submitButtonLoading || disabled}
          onClick={submit}
          sx={{
            boxShadow: 'none',
            textTransform: 'uppercase',
            letterSpacing: 1.2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
          >
            {submitButtonLoading && (
              <CircularProgress
                size={20}
                thickness={4}
                color="grey"
                disableShrink
              />
            )}
            <span>{submitButtonText}</span>
          </Stack>
        </Button>
      )}
    </Stack>
  );
};

ActionButtons.propTypes = {
  closeButtonText: PropTypes.string,
  submitButtonText: PropTypes.string,
  submitButtonLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  submit: PropTypes.func,
  close: PropTypes.func,
};

export const CancelButton = (props) => {
  const { cancelButtonText = 'Cancel', cancel = () => {} } = props;

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ mt: 3, mb: 2 }}
    >
      <Button
        fullWidth
        variant="ghost"
        color="inherit"
        onClick={cancel}
        sx={{ textTransform: 'uppercase' }}
      >
        {cancelButtonText}
      </Button>
    </Stack>
  );
};

CancelButton.propTypes = {
  cancelButtonText: PropTypes.string,
  cancel: PropTypes.func,
};

DrawerContent.Wrapper = Wrapper;
DrawerContent.Content = Content;
DrawerContent.Header = Header;
DrawerContent.ActionButtons = ActionButtons;
DrawerContent.CancelButton = CancelButton;
